import { useEffect, useState, type ChangeEvent, type ReactNode } from "react";
import { Avatar, Btn, Dropdown, Icon, IconBtn, Input, MoreMenu, Pill, Tab, Toggle, T, alpha } from "@v0y4ge/jrni-ui";
import { StatTile } from "../../../shell/composites";
import { can, isAdmin } from "../../../shell/access";
import { AuditTrailDrawer } from "../../../shell/AuditTrail";
import { useNav } from "../../../shell/router";
import { eventActions } from "../eventActions";
import { getEventTickets } from "./event-tickets";
import { EventShareTab } from "./EventShareTab";
import type { UIEvent } from "../../../lib/events-adapter";
import { useT } from "../../../lib/i18n";
import { checkInGuest, creation } from "@v0y4ge/api-client";
import { upsertEvent, refetchEvents } from "../useEvents";
import { useStaff } from "../../staff/useStaff";
import { toast } from "../../../shell/toast";
import { useGuests, type Guest } from "../../guests/useGuests";
import { useCustomerNoun, useIcu } from "../../customers/customers-shared";

/* ── Small drawer-local layout helpers (verbatim from the prototype) ─────── */
function DetailSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <h4 style={{ margin: "0 0 4px", fontSize: 12, fontWeight: 700, color: T.soft, textTransform: "uppercase", letterSpacing: 0.04 }}>{title}</h4>
      <div>{children}</div>
    </div>
  );
}
function KV({ k, v }: { k: string; v: ReactNode }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 13, padding: "8px 0", borderBottom: `1px solid ${T.border}` }}>
      <span style={{ color: T.soft }}>{k}</span>
      <span style={{ color: T.n1, fontWeight: 500, textAlign: "right" }}>{v}</span>
    </div>
  );
}

/* ── Quick settings accordion ─────────────────────────────────────────── */
function SettingRow({ label, sub }: { label: string; sub: string }) {
  // Addendum item 4 (2026-07-25): these toggles held local-only state and
  // reverted on reload — fake interactivity. The contract declares the shape
  // (EventDetailQuickSettings) but NO client op serves or patches it at
  // 93196e63 → honest-disabled + REQ-083 §1. When the ops land: derive from
  // the DTO, PATCH on change, revert on rejection, re-read on success.
  const t = useT();
  return (
    <div title={t("events.detailOverlay.quickSettingUnavailable", "Not available yet — no quick-settings operation is declared in the wiring kit (REQ-083). This switch will drive the real event record when the op lands.")}
      style={{ display: "flex", alignItems: "center", gap: 12, padding: "6px 0", cursor: "not-allowed", opacity: 0.55 }}>
      <span style={{ pointerEvents: "none" }}><Toggle on={false} /></span>
      <span style={{ flex: 1 }}>
        <span style={{ display: "block", fontSize: 13, fontWeight: 600, color: T.n1 }}>{label}</span>
        <span style={{ display: "block", fontSize: 11, color: T.soft, marginTop: 1 }}>{sub}</span>
      </span>
      <Pill tone="neutral">{t("events.detailOverlay.pendingOp", "Pending op")}</Pill>
    </div>
  );
}
function QuickSettings({ live }: { live: boolean }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  return (
    <div style={{ border: `1px solid ${T.border}`, borderRadius: 8, background: T.white }}>
      <Btn variant="ghost" onClick={() => setOpen((o) => !o)} aria-expanded={open} style={{ width: "100%", background: "transparent", border: "none", height: "auto", padding: "12px 14px", display: "flex", alignItems: "center", justifyContent: "space-between", textAlign: "left", boxShadow: "none" }}>
        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Icon.settings size={14} />
          <span style={{ fontSize: 13, fontWeight: 600, color: T.n1 }}>{t("events.detailOverlay.quickSettings", "Quick settings")}</span>
          <span style={{ fontSize: 11, color: T.soft }}>{t("events.detailOverlay.visibilityConfirmations1s", "Visibility · confirmations · +1s")}</span>
        </span>
        <span style={{ display: "inline-flex", transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 120ms", color: T.soft }}><Icon.chev dir="down" size={14} /></span>
      </Btn>
      {open ? (
        <div style={{ padding: "4px 14px 14px", display: "flex", flexDirection: "column", gap: 10, borderTop: `1px solid ${T.border}` }}>
          <SettingRow label={t("events.detailOverlay.public", "Public")} sub={t('events.detailOverlay.sub')} />
          <SettingRow label={t("events.detailOverlay.autoConfirmRegistrations", "Auto-confirm registrations")} sub={t('events.detailOverlay.sub2')} />
          <SettingRow label={t("events.detailOverlay.allow1Invitations", "Allow +1 invitations")} sub={t('events.detailOverlay.sub3')} />
          {live ? <p style={{ margin: "4px 0 0", fontSize: 11, color: T.soft, fontStyle: "italic" }}>{t("events.detailOverlay.thisEventIsLiveChangesApplyToNewRegistrationsOnly", "This event is live — changes apply to new registrations only.")}</p> : null}
        </div>
      ) : null}
    </div>
  );
}

/* ── Host (event organiser) ───────────────────────────────────────────────
 *
 * Editable here because this kit carries no event-creation wizard — the
 * Details step that used to own the Host dropdown is not part of the composed
 * source, so the detail drawer is the only surface an operator can reach.
 *
 * WHAT THE FIELD ACTUALLY IS: `staffName` on the event row — a denormalized
 * display label the events worker accepts on PATCH, explicitly "NOT a link
 * into the canonical staff model" (api-contracts/src/events.ts). The picker is
 * therefore populated FROM staff so an operator cannot fat-finger a name that
 * matches nobody, but what travels is the string. See the note in the reply
 * about what My Day needs beyond this.
 *
 * A host the staff list does not carry (a legacy label, a member since
 * deactivated) stays in the options as-is rather than being silently rewritten
 * to someone else or blanked.
 */
function HostRow({ ev }: { ev: UIEvent }) {
  const t = useT();
  const { members } = useStaff();
  const writable = can("events.write");
  /* `—` is the adapter's placeholder for an absent staffName, not a value. */
  const current = ev.host && ev.host !== "—" ? ev.host : "";
  const [value, setValue] = useState(current);
  const [busy, setBusy] = useState(false);
  // A fresh row from a reload (or another surface's edit) wins over local state.
  useEffect(() => { setValue(current); }, [current]);

  if (!writable) return <KV k={t("events.detailOverlay.host", "Host")} v={ev.host} />;

  const active = members.filter((m) => (m.status ?? "active") === "active");
  const names = active.map((m) => m.name || m.email).filter(Boolean);
  const options = [{ value: "", label: t("events.detailOverlay.noHost", "No host") }]
    .concat(names.map((n) => ({ value: n, label: n })))
    // The stored host, kept verbatim when staff no longer carries it.
    .concat(current && names.indexOf(current) === -1 ? [{ value: current, label: current }] : []);

  const commit = (next: string) => {
    if (next === value) return;
    const prev = value;
    setValue(next);
    setBusy(true);
    /* `staffName: ""` is how a host is cleared — the field is optional on the
       PATCH input and the list hides a blank host, so an empty string is a
       real, meaningful value here rather than a missing one. */
    void creation.updateEvent(ev.id, { staffName: next } as never)
      .then(() => {
        upsertEvent({ ...ev, host: next || "—" });
        toast({ title: next
          ? t("events.detailOverlay.hostSaved", "Host updated")
          : t("events.detailOverlay.hostCleared", "Host cleared") });
      })
      .catch((e) => {
        console.warn("updateEvent(staffName) failed:", e);
        setValue(prev);
        /* A rejected promise here is NOT proof the write was refused: the client
           parses the response through `StudioEventDto`, so a 200 whose body is
           shaped differently rejects too. Re-read rather than trusting the
           local revert — the server row is the only thing that knows. */
        void refetchEvents();
        toast({ title: t("events.detailOverlay.hostSaveFailed", "Couldn't change the host"),
          subtitle: t("events.detailOverlay.nothingWasSaved", "Nothing was saved — retry in a moment."), tone: "danger" });
      })
      .finally(() => setBusy(false));
  };

  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, fontSize: 13, padding: "8px 0", borderBottom: `1px solid ${T.border}` }}>
      <span style={{ color: T.soft }}>{t("events.detailOverlay.host", "Host")}</span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 8, minWidth: 0 }}>
        {value ? <Avatar size={20} initials={value.split(" ").map((w) => w[0]).join("").slice(0, 2)} color={ev.hue} /> : null}
        <Dropdown options={options} value={value} onChange={commit} disabled={busy}
          aria-label={t("events.detailOverlay.host", "Host")} style={{ minWidth: 200 }} />
      </span>
    </div>
  );
}

/* ── Overview tab ─────────────────────────────────────────────────────── */
function EventOverviewTab({ ev }: { ev: UIEvent }) {
  const t = useT();
  const tickets = getEventTickets(ev);
  const paid = tickets.filter((t) => t.price > 0);
  const revenue = paid.reduce((sum, t) => sum + t.price * t.sold, 0);
  const utilPct = ev.capacity ? Math.round((ev.registered / ev.capacity) * 100) : 0;
  // Addendum item 4: the "+N today" delta was hash-derived (charCodeAt % 9) — a
  // generated value presented as measured; no DTO field backs a daily delta, so
  // the tile shows real utilisation instead.
  const expected = Math.max(0, ev.registered - ev.checked);
  const thirdTile = revenue > 0
    ? <StatTile label={t("events.detailOverlay.revenue", "Revenue")} value={`$${revenue.toLocaleString()}`} delta={`${paid.reduce((n, t) => n + t.sold, 0)} paid`} accent={T.orange1} icon={<Icon.ticket size={14} />} />
    : ev.waitlist > 0
      ? <StatTile label={t("events.detailOverlay.waitlist", "Waitlist")} value={`${ev.waitlist}`} delta="Ready to promote" accent={T.tPurple} icon={<Icon.users size={14} />} />
      : <StatTile label={t("events.detailOverlay.capacity", "Capacity")} value={`${utilPct}%`} delta={`${Math.max(0, ev.capacity - ev.registered)} seats left`} accent={T.p1} icon={<Icon.ticket size={14} />} />;
  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
        <StatTile label={t("events.detailOverlay.registered", "Registered")} value={`${ev.registered}`} delta={`${utilPct}% of cap`} icon={<Icon.users size={14} />} />
        <StatTile label={t("events.detailOverlay.checkedIn", "Checked in")} value={`${ev.checked}`} delta={ev.registered ? `${Math.round((ev.checked / ev.registered) * 100)}% of registered` : "—"} accent={T.green1} icon={<Icon.check size={14} />} />
        {thirdTile}
      </div>
      <DetailSection title={t("events.detailOverlay.when", "When")}><KV k="Date" v={ev.date} /><KV k="Time" v={ev.time} /><KV k="Doors open" v="30 min early" /></DetailSection>
      <DetailSection title={t("events.detailOverlay.where", "Where")}><KV k="Location" v={ev.location} /><HostRow ev={ev} /><KV k="Capacity" v={`${ev.capacity} seats${ev.waitlist > 0 ? ` · ${ev.waitlist} waitlist` : ""}`} /></DetailSection>
      <DetailSection title={t("events.detailOverlay.capacity", "Capacity")}>
        <div style={{ height: 10, background: T.n5, borderRadius: 999, overflow: "hidden", display: "flex" }}>
          <div style={{ height: "100%", width: `${ev.capacity ? (ev.checked / ev.capacity) * 100 : 0}%`, background: T.green1 }} />
          <div style={{ height: "100%", width: `${ev.capacity ? Math.max(0, ((ev.registered - ev.checked) / ev.capacity) * 100) : 0}%`, background: T.p1 }} />
        </div>
        <div style={{ fontSize: 12, color: T.soft, marginTop: 6 }}>{ev.checked} {t("events.detailOverlay.checkedIned949840", "checked in ·")}{expected} {t("events.detailOverlay.expected", "expected ·")}{ev.waitlist} {t("events.detailOverlay.onWaitlist", "on waitlist")}</div>
      </DetailSection>
      {ev.status !== "past" ? <QuickSettings live={ev.status === "live"} /> : null}
    </>
  );
}

/* ── Tickets tab ──────────────────────────────────────────────────────── */
function EventTicketsTab({ ev }: { ev: UIEvent }) {
  const t = useT();
  const tickets = getEventTickets(ev);
  const palette = [T.tLightBlue, T.tPurple, T.tPink, T.tOrange, T.tGreen, T.tTurquoise];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {tickets.map((tk, i) => (
        <div key={tk.id} style={{ padding: 14, border: `1px solid ${T.border}`, borderRadius: 6, display: "flex", gap: 12, alignItems: "center" }}>
          <span style={{ width: 4, alignSelf: "stretch", background: palette[i % palette.length], borderRadius: 999 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: T.n1 }}>{tk.name}</div>
            <div style={{ fontSize: 12, color: T.soft }}>{tk.desc}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.n1 }}>{tk.sold} <span style={{ color: T.soft, fontWeight: 400 }}>/ {tk.capacity}</span></div>
            <div style={{ fontSize: 11, color: T.soft }}>{tk.price === 0 ? t('events.detailOverlay.text') : /* TODO(i18n): replace template-literal with t('events.detailOverlay.text2', { ... }) */`$${tk.price}`}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Registrations tab ─────────────────────────────────────────────────
 * The event's REAL roster — same data source AND the same Check-in action as
 * Event Day: useGuests + checkInGuest, GET/PATCH /v1/admin/events/:id/guests
 * via the typed client. Previously this tab called a separate, differently-
 * shaped /registrations route with no serving-engine binding — it 404'd, and
 * a bug in the tab's own effect (a `live`-flag closure raced against the
 * fast rejection so the failure was swallowed) left it stuck on "Loading…"
 * forever instead of showing the honest empty/error state Event Day always
 * has. Reusing useGuests fixes both: same proven data path, same real
 * check-in action, no separate fetch to get wrong.
 *
 * VOCABULARY (2026-09-14, operator): "guest" is retired — there are only
 * customers, and the tenant names them ("Students" in Capture). The wire
 * (`useGuests`, `checkInGuest`, the /guests route) keeps its names; only what
 * an operator reads changes, and it reads the tenant's own noun. */
function EventGuestsTab({ ev }: { ev: UIEvent }) {
  const t = useT();
  const icu = useIcu();
  const { nounPlural } = useCustomerNoun();
  const nav = useNav();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [checkingId, setCheckingId] = useState<string | null>(null);
  const { guests, loading, reload } = useGuests({ eventId: ev.id, limit: 200 });

  const isCheckedIn = (g: Guest) => g.status === "checked-in";
  const inBucket = (g: Guest) =>
    status === "all" ? true
      : status === "checked-in" ? isCheckedIn(g)
      : status === "waitlist" ? g.status === "waitlist"
      : g.status === "registered";
  const needle = q.trim().toLowerCase();
  const matches = (g: Guest) => !needle || [g.name, g.email, g.company, g.ticket].some((v) => typeof v === "string" && v.toLowerCase().includes(needle));
  const shown = guests.filter((g) => inBucket(g) && matches(g));

  const counts: Record<string, number> = {
    all: guests.length,
    "checked-in": guests.filter(isCheckedIn).length,
    registered: guests.filter((g) => g.status === "registered").length,
    waitlist: guests.filter((g) => g.status === "waitlist").length,
  };

  const chip = (id: string, label: string) => (
    <Btn key={id} variant="secondary" onClick={() => setStatus(id)} style={{ height: "auto", padding: "4px 10px", borderRadius: 999, border: `1px solid ${status === id ? T.p1 : T.border}`, background: status === id ? T.p1 : T.white, color: status === id ? T.white : T.n2, fontSize: 12, fontWeight: 600, boxShadow: "none" }}>
      {label}<span style={{ opacity: 0.7, fontWeight: 500 }}>{counts[id]}</span>
    </Btn>
  );

  const initialsOf = (name: string) => name.trim().split(/\s+/).slice(0, 2).map((w) => w[0] ?? "").join("").toUpperCase() || "?";

  const doCheckIn = async (g: Guest) => {
    setCheckingId(g.id);
    try { await checkInGuest(ev.id, g.id); reload(); }
    catch (err) { console.warn("checkInGuest failed:", err); }
    finally { setCheckingId(null); }
  };

  const body = () => {
    if (loading && guests.length === 0) {
      return <div style={{ padding: "24px", textAlign: "center", color: T.soft, fontSize: 12.5 }}>{t("common.states.loading", "Loading…")}</div>;
    }
    if (shown.length === 0) {
      return (
        <div style={{ padding: "24px", textAlign: "center", color: T.soft, fontSize: 12.5 }}>
          {guests.length === 0
            ? t("events.detailOverlay.noRegistrationsYet", "No one has registered for this event yet.")
            : t("events.detailOverlay.noRegistrationsMatch", "No registrations match this search.")}
        </div>
      );
    }
    return (
      <div style={{ display: "flex", flexDirection: "column", border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden" }}>
        {shown.map((g, i) => (
          <div key={g.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", borderTop: i === 0 ? "none" : `1px solid ${T.border}`, background: T.white }}>
            <Avatar initials={initialsOf(g.name)} alt={g.name} size={26} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{g.name}</div>
              <div style={{ fontSize: 11.5, color: T.soft, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{g.ticket || g.email}{g.company ? ` · ${g.company}` : ""}</div>
            </div>
            {can("guests.checkin") && g.status !== "waitlist" ? (
              <Btn variant="secondary" disabled={checkingId === g.id || isCheckedIn(g)} onClick={() => doCheckIn(g)}
                style={{ height: 28, padding: "0 12px", borderRadius: 999, border: `1.5px solid ${isCheckedIn(g) ? T.green1 : T.n4}`, background: isCheckedIn(g) ? T.green2 : T.white, color: isCheckedIn(g) ? T.green1 : T.n2, fontSize: 12, fontWeight: 600, cursor: isCheckedIn(g) ? "default" : "pointer", flexShrink: 0, opacity: checkingId === g.id ? 0.6 : 1, boxShadow: "none" }}>
                {isCheckedIn(g) ? t("events.detailOverlay.checkedIn", "✓ Checked in") : checkingId === g.id ? "…" : t("eventday.eventDay.checkIn", "Check in")}
              </Btn>
            ) : (
              <Pill tone={g.status === "waitlist" ? "warn" : "neutral"}>
                {g.status === "waitlist" ? t("events.detailOverlay.waitlist", "Waitlist") : t("events.detailOverlay.registered", "Registered")}
              </Pill>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <Input full size="sm" leading={<Icon.search size={12} />} placeholder={icu("events.detailOverlay.searchRegistrations", "Search the {nounPlural} registered for this event…", { nounPlural: nounPlural.toLowerCase() })} value={q} onChange={(e: ChangeEvent<HTMLInputElement>) => setQ(e.target.value)} />
        {can("reports.export") ? <Btn variant="secondary" size="sm" icon={<Icon.csv size={12} />} onClick={() => eventActions.exportGuestList(ev)}>{t("common.actions.export", "Export")}</Btn> : null}
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {chip("all", "All")}
        {chip("checked-in", "Checked in")}
        {chip("registered", "Registered")}
        {counts.waitlist > 0 ? chip("waitlist", "Waitlist") : null}
      </div>
      {body()}
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Btn variant="secondary" size="sm" icon={<Icon.arrow dir="right" size={13} />} onClick={() => { nav.closeEvent(); nav.go("customers", { eventId: ev.id }); }}>{icu("events.detailOverlay.openInDirectory", "Open in {nounPlural}", { nounPlural })}</Btn>
      </div>
    </div>
  );
}

/* ── Drawer shell ─────────────────────────────────────────────────────── */
function EventDetailContents({ ev }: { ev: UIEvent }) {
  const t = useT();
  const icu = useIcu();
  const { nounPlural } = useCustomerNoun();
  const nav = useNav();
  const [tab, setTab] = useState<"overview" | "guests" | "tickets" | "share">("overview");
  const [auditOpen, setAuditOpen] = useState(false);
  return (
    <>
      <div style={{ padding: "16px 24px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <span style={{ width: 44, height: 44, borderRadius: 8, background: ev.heroImage ? `url(${ev.heroImage}) center/cover` : ev.hue, color: T.white, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 18, overflow: "hidden" }}>
            {ev.heroImage ? null : ev.title.split(" ").map((w) => w[0]).slice(0, 2).join("")}
          </span>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: T.n1, margin: 0 }}>{ev.title}</h2>
              {ev.status === "live" ? <Pill tone="success" dot>{t("common.status.live", "Live")}</Pill>
                : ev.status === "canceled" ? <Pill tone="danger">{t("common.status.cancelled", "Cancelled")}</Pill>
                : ev.status === "draft" ? <Pill>{t("common.status.draft", "Draft")}</Pill>
                : ev.status === "past" ? <Pill tone="neutral">{t("events.detailOverlay.ended", "Ended")}</Pill>
                : <Pill tone="primary">{t("common.status.upcoming", "Upcoming")}</Pill>}
            </div>
            <div style={{ fontSize: 12, color: T.soft, marginTop: 2 }}>{ev.template}{ev.template && ev.location ? " · " : ""}{ev.location}</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          <IconBtn label={t("common.actions.edit", "Edit")} onClick={() => eventActions.editEvent(ev, nav)}><Icon.edit size={14} /></IconBtn>
          <IconBtn label={t("events.detailOverlay.duplicate", "Duplicate")} onClick={() => eventActions.duplicateEvent(ev)}><Icon.copy size={14} /></IconBtn>
          <MoreMenu label={t("events.detailOverlay.moreEventActions", "More event actions")} size={36} iconSize={16} width={260} header={ev.title}
            items={[
              { icon: <Icon.external size={14} />, label: t("events.detailOverlay.openLandingPage", "Open landing page"), sub: "Opens in a new tab", onClick: () => eventActions.openLandingPage(ev, nav) },
              { icon: <Icon.link size={14} />, label: t("events.detailOverlay.copyPublicUrl", "Copy public URL"), sub: "Share the registration link", onClick: () => eventActions.copyPublicUrl(ev) },
              { icon: <Icon.qr size={14} />, label: t("events.detailOverlay.showCheckInQr", "Show check-in QR"), sub: t("events.detailOverlay.comingSoon", "Coming soon"), onClick: () => eventActions.showCheckInQR(ev) },
              "divider",
              { icon: <Icon.layers size={14} />, label: t("events.detailOverlay.saveAsTemplate", "Save as template"), sub: "Reuse this setup for new events", onClick: () => eventActions.saveAsTemplate(ev, nav) },
              { icon: <Icon.copy size={14} />, label: t("events.detailOverlay.duplicateEvent", "Duplicate event"), sub: "Create a copy as a draft", onClick: () => eventActions.duplicateEvent(ev) },
              ...(can("reports.export") ? [{ icon: <Icon.download size={14} />, label: t("events.detailOverlay.exportRegistrations", "Export registrations"), sub: "CSV of all registrations", onClick: () => eventActions.exportGuestList(ev) }] : []),
              ...(can("events.delete") ? ["divider" as const, { icon: <Icon.x size={14} />, label: ev.status === "draft" ? "Discard draft" : "Cancel event", danger: true, sub: ev.status === "draft" ? "Removes this draft" : "Notifies registered guests", onClick: () => eventActions.cancelOrDiscard(ev, nav) }] : []),
              ...((isAdmin() || can("audit.read_object")) ? ["divider" as const, { icon: <Icon.list size={14} />, label: t("events.detailOverlay.viewAuditTrail", "View audit trail"), onClick: () => setAuditOpen(true) }] : []), // #803 \u2014 Event Manager (audit.read_object) can see the trail too, not just System Admin
            ]} />
          <IconBtn label={t("common.actions.close", "Close")} onClick={() => nav.closeEvent()}><Icon.x /></IconBtn>
        </div>
      </div>

      <div style={{ display: "flex", gap: 0, padding: "0 24px", borderBottom: `1px solid ${T.border}` }}>
        <Tab active={tab === "overview"} onClick={() => setTab("overview")}>{t("events.detailOverlay.overview", "Overview")}</Tab>
        <Tab active={tab === "guests"} count={ev.registered} onClick={() => setTab("guests")}>{t("events.detailOverlay.registrations", "Registrations")}</Tab>
        <Tab active={tab === "tickets"} count={getEventTickets(ev).length} onClick={() => setTab("tickets")}>{t("events.detailOverlay.tickets", "Tickets")}</Tab>
        <Tab active={tab === "share"} onClick={() => setTab("share")}>{t("events.detailOverlay.share", "Share")}</Tab>
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
        {tab === "overview" ? <EventOverviewTab ev={ev} /> : null}
        {tab === "guests" ? <EventGuestsTab ev={ev} /> : null}
        {tab === "tickets" ? <EventTicketsTab ev={ev} /> : null}
        {tab === "share" ? <EventShareTab ev={ev} onOpenLanding={() => { nav.closeEvent(); nav.go("landing.editor", { id: ev.id }); }} /> : null}
      </div>
      {auditOpen ? <AuditTrailDrawer target={{ type: "event", id: ev.id, name: ev.title }} onClose={() => setAuditOpen(false)} /> : null}

      <div style={{ padding: "12px 24px", borderTop: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", gap: 8 }}>
        <Btn variant="secondary" icon={<Icon.send size={14} />} onClick={() => { nav.closeEvent(); nav.go("invite", { eventId: ev.id }); }}>{icu("events.detailOverlay.messageRegistered", "Message {nounPlural}", { nounPlural })}</Btn>
        <div style={{ display: "flex", gap: 8 }}>
          <Btn variant="secondary" onClick={() => eventActions.editEvent(ev, nav)}>{t("events.detailOverlay.editEvent", "Edit event")}</Btn>
          <Btn onClick={() => { nav.closeEvent(); nav.go("customers", { eventId: ev.id }); }}>{icu("events.detailOverlay.openInDirectoryArrow", "Open in {nounPlural} →", { nounPlural })}</Btn>
        </div>
      </div>
    </>
  );
}

/** The slide-over drawer. Rendered once in App when nav.drawerEvent is set. */
export function EventOverlay({ ev }: { ev: UIEvent }) {
  const t = useT();
  const nav = useNav();
  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") nav.closeEvent(); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; window.removeEventListener("keydown", onKey); };
  }, [nav]);
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 100, display: "flex", justifyContent: "flex-end", background: alpha(T.n1, 40), animation: "evfadeIn 200ms cubic-bezier(0.4,0,0.2,1)" }} onClick={() => nav.closeEvent()}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 560, maxWidth: "92vw", background: T.white, height: "100%", boxShadow: T.shadowLg, display: "flex", flexDirection: "column", overflow: "hidden", animation: "evslideIn 240ms cubic-bezier(0.4,0,0.2,1)" }}>
        <EventDetailContents ev={ev} />
      </div>
      <style>{t('events.detailOverlay.text3')}</style>
    </div>
  );
}
